import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-border-strong/20", className)}
      {...props}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BADGE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const badgeVariants = cva(
  "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border transition-colors focus:outline-none select-none",
  {
    variants: {
      variant: {
        // Semantic statuses
        default: "border-transparent bg-primary-500 text-text-on-primary",
        neutral: "border-border bg-card-muted text-text-secondary",
        success: "border-success/30 bg-success-soft text-success",
        warning: "border-warning/30 bg-warning-soft text-warning",
        error: "border-error/30 bg-error-soft text-error",
        info: "border-info/30 bg-info-soft text-info",

        // Company graph categories
        strategy:
          "border-category-strategy-border bg-category-strategy-soft text-category-strategy",
        people:
          "border-category-people-border bg-category-people-soft text-category-people",
        work: "border-category-work-border bg-category-work-soft text-category-work",
        sales:
          "border-category-sales-border bg-category-sales-soft text-category-sales",
        finance:
          "border-category-finance-border bg-category-finance-soft text-category-finance",
        operations:
          "border-category-operations-border bg-category-operations-soft text-category-operations",
        innovation:
          "border-category-innovation-border bg-category-innovation-soft text-category-innovation",
        risk: "border-category-risk-border bg-category-risk-soft text-category-risk",
        documents:
          "border-category-documents-border bg-category-documents-soft text-category-documents",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   EMPTY STATE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center p-8 border border-dashed border-border-strong/50 rounded-xl bg-card/50 max-w-md mx-auto my-4",
        className,
      )}
      {...props}
    />
  );
}

export { Skeleton, Badge, badgeVariants, EmptyState };
